import React from 'react'
import { Link } from 'react-router-dom'
import FormSF from '../../../widgets/form/FormSF'
import CardUI from '../../../widgets/layout/CardUI'
import BadgeUI from '../../../widgets/output/BadgeUI'
import SpinnerUI from '../../../widgets/output/SpinnerUI'
import ButtonTinyUI from '../../../widgets/action/ButtonTinyUI'
import LRTableRowUI from '../../../widgets/layout/LRTableRowUI'
import LRTableBodyUI from '../../../widgets/layout/LRTableBodyUI'
import ButtonTinyWithSpinnerUI from '../../../widgets/action/ButtonTinyWithSpinnerUI'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

const isLegacyFolder = (label) =>
  label && label.toLowerCase() === 'legacy files'

const formatMonthYear = (period) => {
  const month = parseInt(period.substring(0, 2), 10)
  const year = period.substring(2, 6)

  if (month < 1 || month > 12 || year.length !== 4) {
    return period
  }

  return `${MONTH_NAMES[month - 1]} ${year}`
}

const getGSTFolderLabel = (label) => {
  const periodRegex = /^(\d{2}\d{4})-(\d{2}\d{4})$/

  if (isLegacyFolder(label) || !periodRegex.test(label)) {
    return label
  }

  const [startPeriod, endPeriod] = label.split('-')
  const startLabel = formatMonthYear(startPeriod)
  const endLabel = formatMonthYear(endPeriod)

  return startLabel === endLabel
    ? startLabel
    : `${startLabel} - ${endLabel}`
}

const getGSTReuploadFolder = (gstin, label) =>
  isLegacyFolder(label) ? label : `${gstin}/${label}`

const GSTDataCollectionUI = ({ context, account,
  onGSTSubmit, gstSubmitted, gstSuccessMsg, gstErrorList, gstFileList,
  onSkipCollectionGST, skipCollectionModeGST,
  gstSkippedSuccessMsg, gstSkippedErrorList, selectedSkipCollectionGST,
  onDownloadFileClick, fileErrorList,
  gstReuploadSubmitted, gstReuploadSuccessMsg, gstReuploadErrorList,
  selectedReuploadFolder, onGSTReuploadClick
}) =>
  <CardUI title='GST data collection' className='g_m_top_100' noUnderline={true}>
    {
      fileErrorList && fileErrorList.length > 0 &&
      <span>{fileErrorList[0].message}</span>
    }
    {
      account.gstList.length === 0 &&
      <span>No GST</span>
    }
    <LRTableBodyUI>
      {
        account.gstList.map((gst) => {
          const gstFilesByLabel = Object.entries(gstFileList || {}).reduce((acc, [label, files]) => {
            const matchingFiles = files.filter(file => file.includes(gst.gstin))
            if (matchingFiles.length > 0) {
              acc[label] = matchingFiles
            }
            return acc
          }, {})
          return (
            <LRTableRowUI key={gst.gstin} context={context} className='g_flex_top'
              left={
                < div className='g_flex_v' >
                  <span>{gst.gstin}<br />{gst.registrationDateF1} @ {gst.state}</span>
                  {
                    Object.entries(gstFilesByLabel).map(([label, files]) => {
                      const reuploadFolder = getGSTReuploadFolder(gst.gstin, label)

                      return (
                        <LRTableRowUI key={`${gst.gstin}-${label}`}
                          context={context} className='g_flex_top'
                          left={
                            <div className='g_flex_v'>
                              <span>
                                {getGSTFolderLabel(label)}
                              </span>
                              {
                                files.map(file =>
                                  <Link
                                    key={file}
                                    className='g_m_top_25'
                                    onClick={onDownloadFileClick}
                                    to='#'
                                    data-type='gst'
                                    data-file-name={file}
                                    data-folder={getGSTReuploadFolder(gst.gstin, label)}
                                  >
                                    {file}
                                  </Link>
                                )
                              }
                            </div>
                          }
                          right={
                            <div className='g_flex_v g_text_right'>
                              {
                                selectedReuploadFolder === reuploadFolder &&
                                gstReuploadSuccessMsg &&
                                <span className='g_success'>{gstReuploadSuccessMsg}</span>
                              }

                              {
                                selectedReuploadFolder === reuploadFolder &&
                                gstReuploadErrorList &&
                                gstReuploadErrorList.map(err =>
                                  <span className='g_error' key={err}>{err}</span>
                                )
                              }
                              <ButtonTinyWithSpinnerUI
                                label='Reupload GST data from S3 to DB'
                                onClick={() => onGSTReuploadClick(gst.gstin, label)}
                                busy={
                                  gstReuploadSubmitted &&
                                  selectedReuploadFolder === reuploadFolder
                                }
                                alignLeft={false}
                              />
                            </div>
                          }
                        />
                      )
                    }
                    )
                  }
                  <span>{gst.failReason}</span>
                  {
                    context.permissions.includes('su_manage') && gst.receiveStatus !== 'collected' &&
                    gst.receiveStatus !== 'skipped' &&
                    <div className='g_m_top_50 g_muted'>
                      If GST data is not available, skip GST collection
                    </div>
                  }
                </div>
              }
              right={
                <div className='g_flex_v g_text_right'>
                  <span className='g_text_right'><BadgeUI label={gst.receiveStatus} /></span>
                  {
                    gst.receiveStatus !== 'not_initiated' &&
                    <span className='g_m_left_50 g_muted'>{gst.statusDateF1}</span>
                  }
                  {
                    context.permissions.includes('su_manage') && gst.receiveStatus !== 'collected' &&
                    gst.receiveStatus !== 'skipped' &&
                    <div className='g_m_top_50 g_text_right'>
                      <div className='g_flex_h g_flex_right'>
                        <ButtonTinyUI label='Skip Collection' rowId={gst.gstin} onClick={onSkipCollectionGST} />
                        <SpinnerUI className='g_m_left_50' show={skipCollectionModeGST && gst.gstin === selectedSkipCollectionGST} />
                      </div>
                      {
                        gst.gstin === selectedSkipCollectionGST && <span>{gstSkippedSuccessMsg}</span>
                      }
                    </div>
                  }
                </div>
              }
            />
          )
        })
      }
    </LRTableBodyUI>

    {
      account.gstList.length > 0 && account.gstList[0].receiveStatus !== 'not_initiated' &&
      context.permissions.includes('su_manage') &&
      <div className='g_flex_h g_flex_top g_m_top_50'>
        <span className='g_muted'>If GST data needs to be collected again, click the button:</span>
        <FormSF context={context} buttonLabel='Enable Collection of GST Data'
          onSubmit={onGSTSubmit} busy={gstSubmitted}
          successMsg={gstSuccessMsg} errorList={gstErrorList}
          inlineButton={true}
        />
      </div>
    }
    {/* <ButtonTinyWithSpinnerUI className='g_m_top_25 g_text_right'
      label='Reupload GST data from S3 to DB'
      onClick={onGSTReuploadClick} alignLeft={false}
      busy={gstReuploadSubmitted}
    /> */}
  </CardUI >

export default GSTDataCollectionUI
